import Game from './Game';
import { Team } from './Team';
import { Duration, TimerNode } from './graph';
import NatsObject from './lib/NatsObject';
import Uuid from './lib/Uuid';
import { Time } from './lib/hlc';

export type TimerData = {
	id: string;
	version: string;
	game_id: string;
	team_id?: string;
	name: string;
	value: number;
	start_event_id?: string;
	direction?: number;
	extra?: any;
}

export enum Direction {
	Up = 1,
	Down = -1,
}

export class Timer extends NatsObject<TimerData> {
	data: TimerNode;
	game: Game;
	team?: Team;

	start_ms?: number;
	value_ms = 0;

	constructor(data: TimerNode, game: Game, team?: Team) {
		super(game.client, `${game.data.id}.timer.${data.id}`);
		this.data = data;
		this.game = game;
		this.team = team;

		this.updateMs();
	}

	updateFromEvent (data: TimerData) {
		const value_ms = data.value * nano / milli;
		this.data = { ...data, value: msToDuration(value_ms) };
		this.updateMs(value_ms);
	}

	updateMs (value_ms = durationToMs(this.data.value)) {
		if (this.data.start_event_id == null) {
			this.start_ms = undefined;
		} else {
			const time = Time.fromUuid(Uuid.fromString(this.data.start_event_id));
			this.start_ms = Number(time.unix_us) * micro / milli;
		}

		this.value_ms = value_ms;
	}

	get isRunning () {
		return this.data.start_event_id != null && this.data.direction != null;
	}

	get value () {
		if (!this.start_ms || !this.data.direction) return this.value_ms;

		const now = Date.now();
		const elapsed = now - this.start_ms + this.client.sync.clock_offset;
		return this.value_ms + elapsed * this.data.direction;
	}

	// Format according to a string like '[[hh:]mm:]ss[.fff]'. Every component is optional and padded to the number of digits as times it appears.
	toString (format = 'hh:mm:ss.fff') {
		const match = format.match(/^(?:(?:(h*):)?(m*):)?(s+)(?:\.(f*))?$/);
		// console.log('format:', format, 'match:', match);
		if (!match) {
			throw new Error('invalid format string');
		}

		function pad (value: number, digits: number) {
			let s = '' + value;
			while (s.length < digits) {
				s = '0' + s;
			}
			return s;
		}

		const hour_digits = match[1]?.length;
		const minute_digits = match[2]?.length;
		const second_digits = match[3].length;
		const subsec_digits = match[4]?.length;

		let value = this.value * milli / sec;

		let s = '';
		if (value < 0) {
			s += '-';
			value = -value;
		}

		if (hour_digits) {
			s += pad(Math.floor(value / 3600), hour_digits) + ':';
			value = value % 3600;
		}
		if (minute_digits) {
			s += pad(Math.floor(value / 60), minute_digits) + ':';
			value = value % 60;
		}
		s += pad(Math.trunc(value), second_digits);
		if (subsec_digits) {
			const mult = 10 ** subsec_digits;
			s += '.' + pad(Math.trunc(value * mult) % mult, subsec_digits);
		}

		return s;
	}

	async start (direction: Direction) {
		return await this.client.request<TimerData>('timer.start', {
			id: this.data.id,
			version: this.data.version,
			direction,
		});
	}

	async stop () {
		return await this.client.request<TimerData>('timer.stop', {
			id: this.data.id,
			version: this.data.version,
		});
	}

	async set (value: number) {
		return await this.client.request<TimerData>('timer.set', {
			id: this.data.id,
			version: this.data.version,
			value,
		});
	}
}

const milli = 1;
const micro = milli / 1000;
const nano = micro / 1000;
const sec = 1000 * milli;
const min = 60 * sec;
const hour = 60 * min;
const day = 24 * hour;
const month = 30 * day;
const year = 12 * month;

function durationToMs (dur: Duration) {
	return dur.years * year +
		dur.months * month +
		dur.hours * hour +
		dur.days * day +
		dur.minutes * min +
		dur.seconds * sec;
}

function msToDuration (value: number): Duration {
	const years = Math.floor(value / year);
	value %= year;
	const months = Math.floor(value / month);
	value %= month;
	const days = Math.floor(value / day);
	value %= day;
	const hours = Math.floor(value / hour);
	value %= hour;
	const minutes = Math.floor(value / min);
	value %= min;
	const seconds = value / sec;

	return { years, months, days, hours, minutes, seconds };
}

export { Timer as default };
